import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, CheckCircle2, XCircle, Loader2, KeyRound, Eye, EyeOff, Bug, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const StatCard = ({ label, value, tone = 'slate', testId }) => (
  <div className={`rounded-xl border p-4 border-${tone}-200 bg-${tone}-50/40`} data-testid={testId}>
    <p className={`text-xs font-semibold uppercase tracking-wider text-${tone}-700`}>{label}</p>
    <p className={`text-3xl font-bold text-${tone}-900 mt-1 tabular-nums`}>{value}</p>
  </div>
);

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const SecurityPage = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [logins, setLogins] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [honeypot, setHoneypot] = useState(null);
  const [bans, setBans] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Change password
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast({ title: 'Passwords don\'t match', variant: 'destructive' });
      return;
    }
    if (pwForm.next.length < 8) {
      toast({ title: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(`${API}/api/admin/security/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { headers: auth() });
      toast({ title: 'Password updated', description: 'You\'ll stay logged in on this device.' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast({ title: 'Change failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setPwBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, k, hs, hb] = await Promise.all([
        axios.get(`${API}/api/admin/security/stats`, { headers: auth() }),
        axios.get(`${API}/api/admin/security/logins?limit=100${filter ? `&outcome=${filter}` : ''}`, { headers: auth() }),
        axios.get(`${API}/api/admin/security/lockouts`, { headers: auth() }),
        axios.get(`${API}/api/admin/security/honeypot/stats`, { headers: auth() }),
        axios.get(`${API}/api/admin/security/honeypot/bans?limit=50`, { headers: auth() }),
      ]);
      setStats(s.data);
      setLogins(l.data.logins || []);
      setLockouts(k.data.lockouts || []);
      setHoneypot(hs.data);
      setBans(hb.data.bans || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const clearLockout = async (ip) => {
    if (!window.confirm(`Clear all lockouts for IP ${ip}?`)) return;
    await axios.post(`${API}/api/admin/security/lockouts/clear/${encodeURIComponent(ip)}`, null, { headers: auth() });
    load();
  };

  const clearBan = async (ip) => {
    if (!window.confirm(`Unban IP ${ip}?`)) return;
    await axios.post(`${API}/api/admin/security/honeypot/bans/clear/${encodeURIComponent(ip)}`, null, { headers: auth() });
    toast({ title: `Unbanned ${ip}` });
    load();
  };

  const clearAllBans = async () => {
    if (!window.confirm(`Clear ALL honeypot bans? This lets every previously-blocked IP back in.`)) return;
    const { data } = await axios.post(`${API}/api/admin/security/honeypot/bans/clear`, null, { headers: auth() });
    toast({ title: `Cleared ${data.cleared} bans` });
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="security-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="text-cyan-500" size={26} /> Security
        </h1>
        <p className="text-slate-600 mt-1">Recent admin logins, active lockouts, and top failing IPs.</p>
      </div>

      {/* Change password */}
      <form onSubmit={changePassword} className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-4 mb-6" data-testid="change-password-panel">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="text-cyan-600" size={18} />
          <h2 className="text-sm font-bold text-slate-900">Change your admin password</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-2 items-end">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Current password</label>
            <Input type={showPw ? 'text' : 'password'} required value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} data-testid="pw-current-input" autoComplete="current-password" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">New password (min 8 chars)</label>
            <Input type={showPw ? 'text' : 'password'} required minLength={8} value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} data-testid="pw-new-input" autoComplete="new-password" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Confirm new password</label>
            <Input type={showPw ? 'text' : 'password'} required minLength={8} value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="pw-confirm-input" autoComplete="new-password" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button type="submit" disabled={pwBusy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="pw-change-btn">
            {pwBusy ? 'Updating…' : 'Update password'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPw((v) => !v)} className="text-slate-500">
            {showPw ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
            {showPw ? 'Hide' : 'Show'}
          </Button>
        </div>
      </form>

      {loading && <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={28} /></div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="security-stats">
          <StatCard label="Logins 24h" value={stats.total_24h} tone="slate" testId="stat-total-24" />
          <StatCard label="Success 24h" value={stats.success_24h} tone="emerald" testId="stat-success-24" />
          <StatCard label="Failures 24h" value={stats.failures_24h} tone={stats.failures_24h > 0 ? 'amber' : 'slate'} testId="stat-fail-24" />
          <StatCard label="Failures 7d" value={stats.failures_7d} tone={stats.failures_7d > 5 ? 'rose' : 'slate'} testId="stat-fail-7" />
          <StatCard label="Active lockouts" value={stats.active_lockouts} tone={stats.active_lockouts > 0 ? 'rose' : 'slate'} testId="stat-lockouts" />
        </div>
      )}

      {lockouts.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 mb-6" data-testid="lockouts-panel">
          <h2 className="text-sm font-bold text-rose-900 flex items-center gap-2 mb-3">
            <AlertTriangle size={16} /> Active lockouts ({lockouts.length})
          </h2>
          <div className="space-y-2">
            {lockouts.map((k, i) => (
              <div key={i} className="flex items-center justify-between bg-white/70 border border-rose-100 rounded-lg px-3 py-2 text-sm">
                <div>
                  <span className="font-mono text-rose-900">{k.ip}</span>
                  <span className="text-slate-500"> · attempting </span>
                  <span className="font-semibold">{k.username}</span>
                  <span className="text-slate-500"> · unlocks {fmt(k.locked_until)}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => clearLockout(k.ip)} data-testid={`clear-lockout-${i}`}>Clear</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats?.top_failing_ips_7d?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mb-6">
          <h2 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-2">
            <AlertTriangle size={16} /> Top failing IPs (7 days)
          </h2>
          <div className="grid md:grid-cols-2 gap-2">
            {stats.top_failing_ips_7d.map((ip, i) => (
              <div key={i} className="bg-white/70 border border-amber-100 rounded-lg px-3 py-2 text-sm flex items-center justify-between">
                <span className="font-mono">{ip.ip}</span>
                <span className="text-slate-600"><span className="font-bold text-amber-900">{ip.count}</span> fails · last {fmt(ip.last)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Honeypot: bot/scanner IP bans ---------------- */}
      {honeypot && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4 mb-6" data-testid="honeypot-panel">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-purple-900 flex items-center gap-2">
              <Bug size={16} /> Honeypot — auto-banned scanner IPs
            </h2>
            {honeypot.active_bans > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllBans}
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                data-testid="honeypot-clear-all-btn"
              >
                <Trash2 size={14} className="mr-1" /> Clear all bans
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Active bans" value={honeypot.active_bans} tone={honeypot.active_bans > 0 ? 'rose' : 'slate'} testId="stat-honeypot-active" />
            <StatCard label="New 24h" value={honeypot.new_24h} tone={honeypot.new_24h > 0 ? 'amber' : 'slate'} testId="stat-honeypot-24" />
            <StatCard label="Total hits" value={honeypot.total_hits} tone="slate" testId="stat-honeypot-hits" />
            <StatCard label="Bans (all time)" value={honeypot.total_bans} tone="slate" testId="stat-honeypot-total" />
          </div>

          {honeypot.top_paths?.length > 0 && (
            <div className="mb-4">
              <p className="text-[11px] font-semibold text-purple-800 uppercase tracking-wider mb-2">Most-scanned trap paths</p>
              <div className="grid md:grid-cols-2 gap-2">
                {honeypot.top_paths.map((p, i) => (
                  <div key={i} className="bg-white/70 border border-purple-100 rounded-lg px-3 py-2 text-xs flex items-center justify-between">
                    <span className="font-mono text-slate-700 truncate mr-2" title={p.path}>{p.path}</span>
                    <span className="text-purple-900 font-bold shrink-0">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bans.length > 0 ? (
            <div>
              <p className="text-[11px] font-semibold text-purple-800 uppercase tracking-wider mb-2">Currently blocked ({bans.length})</p>
              <div className="max-h-64 overflow-y-auto space-y-2" data-testid="honeypot-bans-list">
                {bans.map((b, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/70 border border-purple-100 rounded-lg px-3 py-2 text-sm gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-purple-900">{b.ip}</span>
                      <span className="text-slate-500"> · </span>
                      <span className="font-semibold">{b.hits} hit{b.hits === 1 ? '' : 's'}</span>
                      <span className="text-slate-500"> · unbans {fmt(b.ban_until)}</span>
                      <div className="text-[11px] text-slate-500 font-mono truncate" title={b.last_path}>
                        last scan: {b.last_path || '—'}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => clearBan(b.ip)} data-testid={`honeypot-unban-${i}`} className="text-slate-500 hover:text-rose-700 shrink-0">
                      Unban
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">No active bans. Bots are either behaving or Cloudflare is catching them upstream.</p>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="logins-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-900">Recent logins</h2>
          <div className="flex gap-1 text-xs">
            <button onClick={() => setFilter('')} className={`px-2.5 py-1 rounded-md ${filter === '' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="filter-all">All</button>
            <button onClick={() => setFilter('success')} className={`px-2.5 py-1 rounded-md ${filter === 'success' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="filter-success">Success</button>
            <button onClick={() => setFilter('failure')} className={`px-2.5 py-1 rounded-md ${filter === 'failure' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="filter-failure">Failures</button>
          </div>
        </div>
        <div className="max-h-[540px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">When</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Username</th>
                <th className="text-left px-4 py-2 font-semibold">Outcome</th>
                <th className="text-left px-4 py-2 font-semibold">User-agent</th>
              </tr>
            </thead>
            <tbody>
              {logins.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 tabular-nums text-slate-700">{fmt(r.ts)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.ip}</td>
                  <td className="px-4 py-2">{r.username}</td>
                  <td className="px-4 py-2">
                    {r.outcome === 'success'
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14} />success</span>
                      : <span className="inline-flex items-center gap-1 text-rose-700"><XCircle size={14} />failure</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs truncate max-w-xs" title={r.user_agent}>{r.user_agent}</td>
                </tr>
              ))}
              {logins.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-400">No login events match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
